import React, { useEffect, useState } from 'react'
import Slider from 'react-slick'

import { TPageContentProps } from '@/config/types'

import { PrimaryButton } from '@/components/buttons/primaryButton'

interface CircularProgressProps {
  progress: number
}

const CircularProgress = ({ progress }: CircularProgressProps) => (
  <svg width="210" height="210" viewBox="0 0 210 210" fill="none">
    <path
      d="M195 105.352C195 154.863 154.706 195 105 195C55.2944 195 15 154.863 15 105.352C15 55.8416 55.2944 15.7051 105 15.7051C154.706 15.7051 195 55.8416 195 105.352ZM15.9 105.352C15.9 154.368 55.7914 194.103 105 194.103C154.209 194.103 194.1 154.368 194.1 105.352C194.1 56.3367 154.209 16.6016 105 16.6016C55.7914 16.6016 15.9 56.3367 15.9 105.352Z"
      stroke="#F2F2F2"
      strokeWidth="30"
      strokeLinejoin="round"
    ></path>
    <circle
      cx="105"
      cy="105"
      r="90"
      fill="none"
      stroke="url(#paint0_linear_1377_15509)"
      strokeWidth="30"
      strokeLinejoin="round"
      strokeLinecap="round"
      pathLength="1"
      strokeDashoffset="0px"
      strokeDasharray={`${progress / 100} ${1 - progress / 100}`}
      transform="rotate(-90 105 105)"
      style={{ transition: 'stroke-dasharray 0.3s ease' }}
    ></circle>
    <path
      d="M105 10C105 15.5419 105 19.9156 105 21.4097"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
    ></path>
    <defs>
      <linearGradient
        id="paint0_linear_1377_15509"
        x1="105"
        y1="15.7051"
        x2="225.248"
        y2="96.3232"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#6BBCB2"></stop>
        <stop offset="1" stopColor="#92CAA2"></stop>
      </linearGradient>
    </defs>
    <text x="105" y="120" textAnchor="middle" fontSize="32" fill="#000">
      {progress}%
    </text>
  </svg>
)

const StepIndicator = ({ delay = 0 }) => {
  const [startAnimation, setStartAnimation] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setStartAnimation(true)
    }, delay * 1000)

    return () => clearTimeout(timer)
  }, [delay])

  if (!startAnimation) {
    return null
  }

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="12" fill="#23786E" opacity="0">
        <animate
          attributeName="opacity"
          values="0;1"
          dur="1s"
          begin="3s"
          repeatCount="1"
          fill="freeze"
        ></animate>
      </circle>

      <circle
        cx="12"
        cy="12"
        r="10.5"
        fill="none"
        strokeWidth="3"
        className="sc-dHMioH jeOpiX"
        stroke="rgba(35, 120, 110, 1)"
        strokeOpacity="1"
        pathLength="1"
        strokeDashoffset="0px"
        strokeDasharray="0 1px"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0;1"
          dur="3s"
          begin="0s"
          repeatCount="1"
        ></animate>
      </circle>

      <path
        d="M8.5 12.5L11 14.5L15.5 10"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      ></path>
    </svg>
  )
}

export const PreparingPlanType3 = ({
  goToNextPage,
  buttonName,
  speed = 100,
  stepsText,
  customReadyTexts,
}: TPageContentProps) => {
  const [progress, setProgress] = useState(0)
  const [readyText, setReadyText] = useState('')

  useEffect(() => {
    const defaultReadyTexts = [
      'Your program is almost ready...',
      'Not long now!',
      'Your program is ready!',
    ]

    const readyTexts = customReadyTexts || defaultReadyTexts

    if (progress < 33) {
      setReadyText(readyTexts[0])
    } else if (progress < 66) {
      setReadyText(readyTexts[1])
    } else {
      setReadyText(readyTexts[2])
    }

    if (progress < 100) {
      const timer = setTimeout(() => setProgress(progress + 1), speed)
      return () => clearTimeout(timer)
    }
  }, [progress, speed, customReadyTexts])

  const sliderSettings = {
    speed: 500,
    infinite: false,
    autoplay: true,
    autoplaySpeed: 3500,
  }

  return (
    <div className="container">
      <div className="content-wrapper">
        <h1>{readyText}</h1>
        <div className="svg-container">
          <CircularProgress progress={progress} />
        </div>
        <div className={'review-carousel'}>
          <Slider {...sliderSettings} className={'slick'}>
            {stepsText?.map((step, index) => (
              <div key={index} className={'review-container'}>
                <div className="step-container">
                  <StepIndicator delay={index * 3.5} />
                  <div
                    className="step-indicator-description"
                    dangerouslySetInnerHTML={{
                      __html: step.text,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
      {progress === 100 && <PrimaryButton onClick={goToNextPage}>{buttonName}</PrimaryButton>}
    </div>
  )
}
